import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MAX_SCORE_ADDITION } from '@aiscoreboard/contracts';
import { BackspaceIcon } from './Icons';
import { useT } from '../lib/i18n';
import { clientId } from '../lib/api';

interface Props {
  playerName: string;
  disabled: boolean;
  error: string | null;
  onConfirm: (points: number, clientId: string) => Promise<boolean>;
  onClose: () => void;
}

export function ScoreEntryDialog({ playerName, disabled, error, onConfirm, onClose }: Props) {
  const t = useT();
  const titleId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const submitting = useRef(false);
  const attempt = useRef<{ points: number; clientId: string } | null>(null);
  const [pending, setPending] = useState(false);
  const [digits, setDigits] = useState('');
  const points = Number(digits);
  const locked = disabled || pending;
  const append = (digit: string) => {
    if (locked) return;
    setDigits(value => value === '0' ? digit : value.length < 3 ? value + digit : value);
  };
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);
  const submit = async () => {
    if (locked || submitting.current || points < 1 || points > MAX_SCORE_ADDITION) return;
    submitting.current = true;
    setPending(true);
    try {
      if (attempt.current?.points !== points) attempt.current = { points, clientId: clientId() };
      await onConfirm(points, attempt.current.clientId);
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };

  return createPortal(
    <dialog ref={dialog} aria-labelledby={titleId}
      onCancel={event => { event.preventDefault(); if (!pending) onClose(); }}
      className="score-entry-dialog panel fixed inset-0 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%_-_2rem)] max-w-xs overflow-y-auto border-0 bg-hud-bg/90 p-4 text-white backdrop:bg-black/75">
      <form className="flex flex-col gap-3" onSubmit={event => { event.preventDefault(); void submit(); }}
        onKeyDown={event => {
          if (locked) return;
          if (/^\d$/.test(event.key)) { event.preventDefault(); append(event.key); }
          else if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault(); setDigits(value => value.slice(0, -1));
          }
        }}>
        <h2 id={titleId} className="min-h-6 truncate text-center text-base font-bold">
          {t('scoreEntry.title', { name: playerName })}
        </h2>
        <input autoFocus readOnly inputMode="none" aria-label={t('scoreEntry.amount')} value={digits || '0'}
          className="field num-lg h-14 w-full text-center text-3xl" />
        <div className="grid grid-cols-3 gap-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digit => (
            <button type="button" key={digit} disabled={locked || digits.length >= 3}
              className="btn grid h-11 place-items-center bg-white/10 p-0 text-xl tabular-nums" onClick={() => append(digit)}>{digit}</button>
          ))}
          <button type="button" aria-label={t('scoreEntry.clear')} disabled={locked || !digits}
            className="btn grid h-11 place-items-center bg-white/10 p-0 text-xl" onClick={() => setDigits('')}>C</button>
          <button type="button" disabled={locked || digits.length >= 3}
            className="btn grid h-11 place-items-center bg-white/10 p-0 text-xl" onClick={() => append('0')}>0</button>
          <button type="button" aria-label={t('scoreEntry.delete')} disabled={locked || !digits}
            className="btn flex h-11 items-center justify-center bg-white/10" onClick={() => setDigits(value => value.slice(0, -1))}>
            <BackspaceIcon />
          </button>
        </div>
        {error && <p role="alert" className="text-center text-xs text-red-300">{error}</p>}
        <div className="flex items-stretch gap-2">
          <button type="button" disabled={pending} className="btn-ghost min-h-11 min-w-0 flex-1 px-2 text-sm"
            onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" disabled={locked || points < 1 || points > MAX_SCORE_ADDITION}
            className="btn min-h-11 min-w-0 flex-1 bg-white px-2 text-sm text-hud-bg">
            {t(pending ? 'match.saving' : 'scoreEntry.confirm')}
          </button>
        </div>
      </form>
    </dialog>,
    document.body,
  );
}
